import type { Advisor } from '@/types'

/** Advisor time slots are stored and shown in US Eastern Time. */
export const TIMEZONE_LABEL = 'ET'
export const TIMEZONE_ID = 'America/New_York'

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

export type DayPeriod = 'Mornings' | 'Afternoons' | 'Evenings'

export const toDateKey = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${date.getFullYear()}-${month}-${day}`
}

export const fromDateKey = (key: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key)

    if (!match) return null

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))

    return Number.isNaN(date.getTime()) || toDateKey(date) !== key ? null : date
}

/** The next bookable days (starting tomorrow) on the advisor's weekdays. */
export const upcomingDays = (availableDays: Array<string>, from: Date, count = 10, horizon = 42): Array<{ key: string, date: Date }> => {
    const days: Array<{ key: string, date: Date }> = []
    const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1)

    for (let step = 0; step < horizon && days.length < count; step += 1) {
        if (availableDays.includes(WEEKDAYS[cursor.getDay()])) days.push({ key: toDateKey(cursor), date: new Date(cursor) })
        cursor.setDate(cursor.getDate() + 1)
    }

    return days
}

export const periodOf = (time: string): DayPeriod => {
    const hour = Number(time.split(':')[0])

    if (hour < 12) return 'Mornings'
    if (hour < 17) return 'Afternoons'

    return 'Evenings'
}

/** Availability tags used by the advisor filter, such as Mornings or Weekends. */
export const availabilityTags = (advisor: Pick<Advisor, 'timeSlots' | 'availableDays'>): Array<string> => {
    const tags = new Set<string>(advisor.timeSlots.map(periodOf))

    if (advisor.availableDays.some(day => day === 'Sat' || day === 'Sun')) tags.add('Weekends')

    return Array.from(tags)
}

export const formatDayLabel = (date: Date): string => date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

/**
 * Server-side check that a requested call fits the advisor's schedule and
 * falls inside the bookable window. Allows a day of slack for time zones.
 */
export const isBookableSlot = (advisor: Pick<Advisor, 'availableDays' | 'timeSlots'>, dateKey: string, time: string, now = new Date()): boolean => {
    const date = fromDateKey(dateKey)

    if (!date || !advisor.timeSlots.includes(time)) return false
    if (!advisor.availableDays.includes(WEEKDAYS[date.getDay()])) return false

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const daysAhead = Math.round((date.getTime() - today.getTime()) / 86400000)

    return daysAhead >= 0 && daysAhead <= 45
}
